import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { createKitchen } from "../../../redux/actions/kitchenActions";
import { toast } from "react-hot-toast";
import CameraCapture from "../../../components/shared/CameraCapture";
import { useLanguage } from "../../../context/LanguageContext";

import {
  ArrowLeft,
  Save,
  AlertCircle,
  CheckCircle,
  Trash2,
  Upload,
  Camera,
  FileText,
  Recycle,
  ShieldAlert,
  Package,
} from "lucide-react";

const SectionGWasteManagement = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const dispatch = useDispatch();
  const { t } = useLanguage();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraField, setCameraField] = useState(null);

  const [previewImages, setPreviewImages] = useState({
    g2Photo: null,
    g3Document: null,
  });

  const yesNoToInt = (value) => {
    if (value === t.global.yes) return 1;
    if (value === t.global.no) return 2;
    return null;
  };

  const [form, setForm] = useState({
    g1: "",
    g2: "",
    g2Photo: null,
    g3: "",
    g3Document: null,
  });

  const [errors, setErrors] = useState({});

  // Load saved draft if exists
  useEffect(() => {
    const loadSavedData = () => {
      try {
        const savedData = localStorage.getItem("survey_section_g");
        if (savedData) {
          const parsedData = JSON.parse(savedData);
          const { timestamp, canteenInfo, ...formData } = parsedData;
          setForm(formData);
        }
      } catch (err) {
        console.error("Error loading saved data:", err);
      }
    };

    loadSavedData();
  }, []);

  // Auto-save to localStorage
  useEffect(() => {
    const timer = setTimeout(() => {
      if (Object.values(form).some((v) => v !== "" && v !== null)) {
        localStorage.setItem("survey_section_g", JSON.stringify(form));
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [form]);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      const file = files[0];

      // Validate file size immediately
      if (file && file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({
          ...prev,
          [name]: t.global.err_file_size,
        }));
        return;
      }

      // Validate file type
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];
      if (file && !allowedTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          [name]: t.global.err_file_type_long,
        }));
        return;
      }

      setForm((prev) => ({ ...prev, [name]: file }));
      if (file && file.type.startsWith("image/")) {
        const imageUrl = URL.createObjectURL(file);
        setPreviewImages((prev) => ({
          ...prev,
          [name]: imageUrl,
        }));
      }

      // Clear file error if validation passes
      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: undefined }));
      }
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }

    // Clear error when user makes a selection
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required field validations for all radio buttons
    if (!form.g1) newErrors.g1 = t.global.err_a1;
    if (!form.g2) newErrors.g2 = t.global.err_a1;
    if (!form.g3) newErrors.g3 = t.global.err_a1;

    // File validation only if file IS uploaded (optional but recommended)
    if (form.g2Photo && form.g2Photo.size > 5 * 1024 * 1024) {
      newErrors.g2Photo = t.global.err_file_size;
    }
    if (form.g2Photo) {
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];
      if (!allowedTypes.includes(form.g2Photo.type)) {
        newErrors.g2Photo = t.global.err_file_type_long;
      }
    }

    if (form.g3Document && form.g3Document.size > 5 * 1024 * 1024) {
      newErrors.g3Document = t.global.err_file_size;
    }
    if (form.g3Document) {
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];
      if (!allowedTypes.includes(form.g3Document.type)) {
        newErrors.g3Document = t.global.err_file_type_long;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    const isPartial =
      !form.g1 ||
      !form.g2 ||
      !form.g3;

    setIsSubmitting(true);

    try {
      const fd = new FormData();

      // =====================
      // Location & Master IDs
      // =====================
      fd.append("district_id", state?.district_id);
      fd.append("municipality_id", state?.municipality_id);
      fd.append("ward_id", state?.ward_id);
      fd.append("area_id", state?.area_id);
      fd.append("canteen_type_id", state?.canteen_type_id);
      fd.append("reporting_date", new Date().toISOString().split("T")[0]);

      // Add latitude/longitude if available
      if (state?.latitude) fd.append("latitude", state.latitude);
      if (state?.longitude) fd.append("longitude", state.longitude);

      // =====================
      // Section G (Waste Management)
      // =====================
      fd.append("wst_mngemnt_G1", yesNoToInt(form.g1));
      fd.append("wst_mngemnt_G2", yesNoToInt(form.g2));
      if (form.g2 === t.global.yes && form.g2Photo) {
        fd.append("wst_mngemnt_G2_upload", form.g2Photo);
      }

      fd.append("wst_mngemnt_G3", yesNoToInt(form.g3));
      if (form.g3 === t.global.yes && form.g3Document) {
        fd.append("wst_mngemnt_G3_upload", form.g3Document);
      }

      fd.append("current_section", "waste");
      fd.append("reporting_date", new Date().toISOString().split("T")[0]);

      // =====================
      // Dispatch API
      // =====================
      await dispatch(createKitchen(fd));

      // ✅ Save to localStorage with partial flag
      const sectionData = {
        ...form,
        isPartial: isPartial,
        submittedAt: new Date().toISOString(),
      };

      localStorage.setItem("survey_section_g", JSON.stringify(sectionData));

      // ✅ Mark Section G as submitted (could be partial)
      const completedSections = JSON.parse(
        localStorage.getItem("completedSections") || "{}",
      );
      completedSections.g = true;
      localStorage.setItem(
        "completedSections",
        JSON.stringify(completedSections),
      );

      // Show success message
      setShowSuccess(true);

      // Auto-navigate after 2 seconds
      setTimeout(() => {
        setShowSuccess(false);
        navigate("/survey/sections", { state });
      }, 2000);
    } catch (error) {
      console.error("Submission error:", error);

      // Save locally for offline
      const isPartial =
        (form.g2 === t.global.yes && !form.g2Photo) ||
        (form.g3 === t.global.yes && !form.g3Document);

      const sectionData = {
        ...form,
        isPartial: isPartial,
        submittedAt: new Date().toISOString(),
        savedLocally: true,
      };

      localStorage.setItem("survey_section_g", JSON.stringify(sectionData));

      // Mark as completed locally
      const completedSections = JSON.parse(
        localStorage.getItem("completedSections") || "{}",
      );
      completedSections.g = true;
      localStorage.setItem(
        "completedSections",
        JSON.stringify(completedSections),
      );

      toast.error(t.global.alert_saved_locally);
      setIsSubmitting(false);
      navigate("/survey/sections", { state });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Success Message Overlay */}
        {showSuccess && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-8 max-w-md w-full text-center animate-scale-in">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {t.global.success_title}
              </h3>
              <p className="text-gray-600 mb-6">
                Section G submitted successfully.
              </p>
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">
                  {t.global.redirecting}
                </span>
              </div>
              <button
                onClick={() => {
                  setShowSuccess(false);
                  navigate("/survey/sections", { state });
                }}
                className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                {t.global.continue_now}
              </button>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                {t.modules.kitchen.sections.G.title}
              </h2>
              <p className="text-gray-600">
                {t.modules.kitchen.sections.G.subtitle}
              </p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="text-sm bg-teal-100 text-teal-800 px-3 py-1 rounded-full flex items-center">
                <Recycle size={14} className="mr-1" />
                Waste Management Survey
              </div>
              <div className="text-sm bg-blue-100 text-blue-800 px-3 py-1 rounded-full">
                {state?.area_name ? `${t.global.canteen_name_label} ${state.area_name}` : t.global.na}
              </div>
              {state?.canteenId && (
                <div className="text-sm bg-purple-100 text-purple-800 px-3 py-1 rounded-full">
                  {t.global.canteen_id_label} {state.canteenId}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-6">
            {/* G.1 - Waste Segregation System */}
            <div className="border rounded-lg p-4 bg-green-50 border-green-200">
              <label className="block font-medium mb-3 flex items-center">
                <Trash2 size={18} className="mr-2" />
                G.1 {t.modules.kitchen.sections.G.questions.wasteSegregation}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-amber-800 font-medium mb-1">
                  {t.modules.kitchen.sections.G.hints.wasteSegregation.split(":")[0]}:
                </p>
                <ul className="text-sm text-amber-700 list-disc pl-5 space-y-1">
                  <li>Separate bins for dry and wet waste</li>
                  <li>Covered bins with foot-operated lids</li>
                  <li>Clearly labeled waste collection points</li>
                  <li>Adequate number of bins for the facility size</li>
                </ul>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      form.g1 === option
                        ? "bg-blue-100 border-blue-400 shadow-sm"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    } ${errors.g1 ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="g1"
                      value={option}
                      checked={form.g1 === option}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <div className="flex items-center">
                      <span
                        className={`text-lg font-medium ${
                          form.g1 === option ? "text-blue-700" : "text-gray-700"
                        }`}
                      >
                        {option}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.g1 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.g1}
                </p>
              )}
            </div>

            {/* G.2 - IEC Display */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-3 flex items-center">
                <ShieldAlert size={18} className="mr-2" />
                G.2 {t.modules.kitchen.sections.G.questions.iecDisplay}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <p className="text-sm text-gray-600 mb-4">
                {t.modules.kitchen.sections.G.hints.iecDisplay}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      form.g2 === option
                        ? "bg-blue-100 border-blue-400 shadow-sm"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    } ${errors.g2 ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="g2"
                      value={option}
                      checked={form.g2 === option}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <div className="flex items-center">
                      <span
                        className={`text-lg font-medium ${
                          form.g2 === option ? "text-blue-700" : "text-gray-700"
                        }`}
                      >
                        {option}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.g2 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.g2}
                </p>
              )}

              {form.g2 === t.global.yes && (
                <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <label className="block text-sm font-medium mb-3 text-green-800 flex items-center">
                    <Camera size={14} className="mr-2" />
                    Upload picture of IEC display (Optional but recommended):
                  </label>

                  {/* Buttons Row */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <button
                      type="button"
                      onClick={() => {
                        setCameraField("g2Photo");
                        setShowCamera(true);
                      }}
                      className="flex items-center justify-center gap-2 p-3 border-2 border-gray-300 text-gray-600 rounded-lg active:scale-95 active:bg-blue-600 active:text-white transition-all duration-150 ease-out cursor-pointer font-medium"
                    >
                      <Camera size={20} /> {t.global.useCamera}
                    </button>

                    <label
                      htmlFor="g2Photo"
                      className="flex items-center justify-center gap-2 p-3 border-2 border-gray-300 text-gray-600 rounded-lg active:scale-95 active:bg-blue-600 active:text-white transition-all duration-150 ease-out cursor-pointer font-medium"
                    >
                      <Upload size={20} /> {t.global.uploadPhoto}
                    </label>
                  </div>
                  <input
                    type="file"
                    name="g2Photo"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleChange}
                    className="hidden"
                    id="g2Photo"
                  />

                  {/* Preview */}
                  {form.g2Photo ? (
                    <div className="space-y-3 bg-white p-4 rounded-lg shadow">
                      {previewImages.g2Photo && (
                        <img
                          src={previewImages.g2Photo}
                          alt="Preview"
                          className="max-h-48 mx-auto rounded-lg shadow"
                        />
                      )}

                      <div className="flex justify-between items-center">
                        <p className="text-sm break-all">{form.g2Photo.name}</p>

                        <button
                          type="button"
                          onClick={() => {
                            setForm((prev) => ({ ...prev, g2Photo: null }));
                            setPreviewImages((prev) => ({
                              ...prev,
                              g2Photo: null,
                            }));
                          }}
                          className="text-red-600 text-sm font-medium hover:underline"
                        >
                          {t.global.remove}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">{t.global.noPhotoSelected || "No photo selected"}</p>
                  )}
                </div>
              )}
            </div>

            {/* G.3 - Housekeeping Checklist */}
            <div className="border rounded-lg p-4 bg-green-50 border-green-200">
              <label className="block font-medium mb-3 flex items-center">
                <FileText size={18} className="mr-2" />
                G.3 {t.modules.kitchen.sections.G.questions.binCleaning}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <p className="text-sm text-gray-600 mb-4">
                {t.modules.kitchen.sections.G.hints.binCleaning}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      form.g3 === option
                        ? "bg-blue-100 border-blue-400 shadow-sm"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    } ${errors.g3 ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="g3"
                      value={option}
                      checked={form.g3 === option}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <div className="flex items-center">
                      <span
                        className={`text-lg font-medium ${
                          form.g3 === option ? "text-blue-700" : "text-gray-700"
                        }`}
                      >
                        {option}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.g3 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.g3}
                </p>
              )}

              {form.g3 === t.global.yes && (
                <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <label className="block text-sm font-medium mb-4 text-gray-700 flex items-center">
                    <FileText size={14} className="mr-2" />
                    Upload housekeeping record (Optional but recommended):
                  </label>

                  {/* SELECTION BUTTONS */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <button
                      type="button"
                      onClick={() => {
                        setCameraField("g3Document");
                        setShowCamera(true);
                      }}
                      className="flex items-center justify-center gap-2 p-3 border-2 border-gray-300 text-gray-600 rounded-lg active:scale-95 active:bg-blue-600 active:text-white transition-all duration-150 ease-out cursor-pointer font-medium"
                    >
                      <Camera size={20} /> {t.global.useCamera}
                    </button>

                    <label
                      htmlFor="g3Document"
                      className="flex items-center justify-center gap-2 p-3 border-2 border-gray-300 text-gray-600 rounded-lg active:scale-95 active:bg-blue-600 active:text-white transition-all duration-150 ease-out cursor-pointer font-medium"
                    >
                      <Upload size={20} /> {t.global.uploadPhoto}
                    </label>
                  </div>

                  {/* HIDDEN INPUT */}
                  <input
                    type="file"
                    name="g3Document"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleChange}
                    className="hidden"
                    id="g3Document"
                  />

                  {/* PREVIEW */}
                  <div
                    className={`border-2 border-dashed rounded-lg p-6 text-center transition ${
                      form.g3Document
                        ? "border-yellow-400 bg-yellow-50"
                        : "border-gray-300 bg-gray-50"
                    }`}
                  >
                    {form.g3Document ? (
                      <div className="space-y-3">
                        {previewImages.g3Document &&
                          form.g3Document.type.startsWith("image/") && (
                            <img
                              src={previewImages.g3Document}
                              alt="Preview"
                              className="max-h-48 mx-auto rounded-lg shadow"
                            />
                          )}

                        <div className="flex items-center justify-between">
                          <p className="text-gray-700 font-medium text-sm break-all">
                            {form.g3Document.name}
                          </p>

                          <button
                            type="button"
                            onClick={() => {
                              setForm((prev) => ({
                                ...prev,
                                g3Document: null,
                              }));
                              setPreviewImages((prev) => ({
                                ...prev,
                                g3Document: null,
                              }));
                            }}
                            className="ml-4 text-red-600 px-3 py-1 rounded-md hover:bg-red-600 hover:text-white transition text-xs font-bold"
                          >
                            {t.global.remove}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-400 italic text-sm">
                        Attach housekeeping record
                      </p>
                    )}
                  </div>

                  {errors.g3Document && (
                    <p className="mt-2 text-sm text-red-600 flex items-center">
                      <AlertCircle size={14} className="mr-1" />
                      {errors.g3Document}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Remarks Field (Optional) */}
            <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
              <label className="block font-medium mb-3 flex items-center">
                <Package size={18} className="mr-2" />
                {t.global.remarks}
              </label>
              <textarea
                name="remarks"
                value={form.remarks || ""}
                onChange={handleChange}
                placeholder={t.global.remarksPlaceholder}
                className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[100px]"
                rows={3}
              />
              <p className="text-sm text-gray-500 mt-2">
                {t.global.remarksOptionalNote}
              </p>
            </div>

            {/* Important Notes */}
            <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-5 w-5 text-amber-500" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-amber-800">
                    <span className="font-medium">{t.global.importantNotes}:</span>
                  </p>
                  <ul className="text-sm text-amber-700 mt-1 list-disc pl-5 space-y-1">
                    <li>
                      {t.global.mandatoryFieldsNote}
                    </li>
                    <li>
                      {t.global.optionalUploadsNote}
                    </li>
                    <li>
                      {t.global.partialFillNote}
                    </li>
                    <li>
                      {t.global.returnLaterNote}
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Waste Management Best Practices */}
            <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded-r-lg">
              <div className="flex">
                <div className="flex-shrink-0">
                  <Recycle className="h-5 w-5 text-green-500" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-green-800 font-medium">
                    ♻️ Waste Management Best Practices
                  </p>
                  <p className="text-sm text-green-700 mt-1">
                    Proper waste segregation prevents contamination, reduces
                    environmental impact, and ensures compliance with hygiene
                    standards. Regular cleaning and documented procedures are
                    essential for food safety.
                  </p>
                </div>
              </div>
            </div>

            {/* Footer Info */}
            <div className="pt-4 border-t border-gray-200">
              <div className="text-center text-sm text-gray-500">
                <span className="text-red-500 font-medium">
                  * Required fields must be completed
                </span>
                <p className="text-sm text-gray-600 mt-1">
                  All other information is optional but recommended for
                  comprehensive waste management assessment.
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between pt-6 border-t">
              <button
                type="button"
                onClick={() => navigate("/survey/sections", { state })}
                className="px-6 py-3 bg-gray-200 rounded-lg hover:bg-gray-300 transition font-medium flex items-center"
                disabled={isSubmitting}
              >
                <ArrowLeft size={18} className="mr-2" />
                {t.global.back_to_sections}
              </button>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={`px-6 py-3 rounded-lg font-medium flex items-center transition ${
                  isSubmitting
                    ? "bg-blue-400 cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    {t.global.submitting}
                  </>
                ) : (
                  <>
                    <Save size={18} className="mr-2" />
                    {t.global.submit_section_g}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Animation */}
      <style jsx>{`
        @keyframes scale-in {
          from {
            transform: scale(0.9);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
      `}</style>
      {showCamera && (
        <CameraCapture
          onCapture={(file) => {
            setForm((prev) => ({
              ...prev,
              [cameraField]: file,
            }));

            const imageUrl = URL.createObjectURL(file);

            setPreviewImages((prev) => ({
              ...prev,
              [cameraField]: imageUrl,
            }));

            setShowCamera(false);
          }}
          onClose={() => setShowCamera(false)}
        />
      )}
    </div>
  );
};

export default SectionGWasteManagement;